/** Persisted feet select a physical room; camera/cut state never participates. */
export interface FloorBandBox {
  x: { min: number; max: number };
  y: { min: number; max: number };
  floor: number;
  ceiling: number;
}

export function floorBandRoomForPosition<T extends FloorBandBox>(boxes: readonly T[], x: number, y: number, z: number): T | null {
  if (![x, y, z].every(Number.isFinite)) return null;
  let winner: T | null = null;
  let area = Infinity;
  let contact = false;
  for (const box of boxes) {
    if (![box.x.min, box.x.max, box.y.min, box.y.max, box.floor, box.ceiling].every(Number.isFinite)
      || box.x.max <= box.x.min || box.y.max <= box.y.min || box.ceiling <= box.floor
      || x < box.x.min || x > box.x.max || y < box.y.min || y > box.y.max || z < box.floor - .01) continue;
    const touchesFloor = Math.abs(z - box.floor) <= .01;
    if (!touchesFloor && z >= box.ceiling - .01) continue;
    const footprint = (box.x.max - box.x.min) * (box.y.max - box.y.min);
    if (touchesFloor !== contact ? touchesFloor : footprint < area) {
      winner = box; area = footprint; contact = touchesFloor;
    }
  }
  return winner;
}

/** A walkable roof: a query-only open-ground rectangle at a roofed room's roof level. */
export interface RoofDeckBox extends FloorBandBox {
  id: string; roofOf: string; roofDeck: true;
  /** A native pitched roof's ridge perch (the eave is the top of its walls). */
  ridge?: true; hipped?: boolean; eaveFt?: number; roomIds?: string[]; roofInventoryId?: string;
  /** The whole bound building (the roof's eave outline), for "on or climbing this roof". */
  roofFootprint?: { minX: number; maxX: number; minY: number; maxY: number };
}

/** Twins of FT3D_ROOF_RIDGE_HALF_WIDTH_FT and ft3d_roof_ridge_kit() (ft3d_floor_band.php). */
export const ROOF_RIDGE_HALF_WIDTH_FT = 2.5;
const ROOF_RIDGE_KITS: Readonly<Record<string, { standFt: number; ridgeHalfFt: number | null }>> = Object.freeze({
  '305044BB-11B2-B565-ABCC-E1A04FBCC6CE': { standFt: 8.3, ridgeHalfFt: null }, // gabled reed thatch
  '97C0FCB5-8943-71DC-E316-469B486702A5': { standFt: 8.3, ridgeHalfFt: null }, // gabled wooden shingles
  '3299FC30-23BA-680F-E47B-C556704F6C77': { standFt: 8.3, ridgeHalfFt: null }, // collapsed wooden shingles
  '10F51ACE-1672-AFA1-1602-99D60DB4A46D': { standFt: 8.2, ridgeHalfFt: 5 },   // slate hipped
  '8412BBD8-8768-84E3-0E58-3494C379FC58': { standFt: 6.9, ridgeHalfFt: 5 },   // terracotta hipped
});

type ShapeBoxLike = FloorBandBox & { id?: unknown; navigationOpenGround?: unknown; roofDeck?: unknown; creation?: Record<string, unknown> | null };

/**
 * Twin of ft_shape_roof_deck_boxes() (includes/functions/janus/encounter/ft3d_floor_band.php) —
 * keep the two in step. Roofed, walled rooms whose roof is open sky; skipped for removed roofs,
 * native pitched roofs (a bound buildingRoofs inventory row), a roof wholly under one stacked
 * storey, and any roof whose plane lies inside another room's volume. Rounded footprints keep the
 * conservative plus shape. `maxLevelFt` keeps only roofs at or below the querying feet.
 */
type RoofBindingLike = { inventoryId?: unknown; meshId?: unknown; roomIds?: unknown; x?: unknown; y?: unknown; z?: unknown; widthFt?: unknown; depthFt?: unknown };
type SceneWallDeckLike = { id?: unknown; axis?: unknown; at?: unknown; span?: { min?: unknown; max?: unknown }; z?: { max?: unknown }; thicknessFt?: unknown };
type RoofDeckShape = { boxes?: readonly ShapeBoxLike[]; authoring?: { buildingRoofs?: Record<string, RoofBindingLike> } | null; sceneWalls?: readonly SceneWallDeckLike[] };
const ROOF_DECK_CACHE = new WeakMap<object, { boxes: unknown; decks: RoofDeckBox[] }>();

export function roofDeckBoxes(shape: RoofDeckShape, maxLevelFt: number | null = null): RoofDeckBox[] {
  // Per shape object (and its box list): the per-frame pose/slab updaters ask every 250 ms.
  let cached = ROOF_DECK_CACHE.get(shape);
  if (!cached || cached.boxes !== shape.boxes) {
    cached = { boxes: shape.boxes, decks: allRoofDeckBoxes(shape) };
    ROOF_DECK_CACHE.set(shape, cached);
  }
  return maxLevelFt === null ? cached.decks : cached.decks.filter(deck => deck.floor <= maxLevelFt + .05);
}

function allRoofDeckBoxes(shape: RoofDeckShape): RoofDeckBox[] {
  const bound = new Set<string>();
  for (const binding of Object.values(shape.authoring?.buildingRoofs ?? {})) {
    if (!binding?.inventoryId) continue;
    for (const roomId of Array.isArray(binding.roomIds) ? binding.roomIds : []) bound.add(String(roomId));
  }
  const structural = (shape.boxes ?? []).filter(box => box && box.roofDeck !== true && box.navigationOpenGround !== true
    && [box.x?.min, box.x?.max, box.y?.min, box.y?.max, box.floor, box.ceiling].every(Number.isFinite)
    && box.creation?.enclosure !== 'none' && box.creation?.environment !== 'exterior-open' && box.creation?.presetId !== 'exterior-open');
  const decks: RoofDeckBox[] = [];
  // DAFT3D S24: the top of a drawn wall at least 2 ft thick (a rampart) is somewhere to stand.
  for (const wall of shape.sceneWalls ?? []) {
    const thickness = Number(wall?.thicknessFt), level = Number(wall?.z?.max), at = Number(wall?.at);
    const min = Number(wall?.span?.min), max = Number(wall?.span?.max);
    if (!(thickness >= 2) || (wall?.axis !== 'x' && wall?.axis !== 'y') || ![level, at, min, max].every(Number.isFinite)) continue;
    const half = thickness / 2;
    decks.push({ id: `wall-top:${String(wall.id ?? '')}`, roofOf: String(wall.id ?? ''), roofDeck: true, floor: level, ceiling: level + 1000,
      x: wall.axis === 'x' ? { min: at - half, max: at + half } : { min, max },
      y: wall.axis === 'x' ? { min, max } : { min: at - half, max: at + half } });
  }
  // Native pitched roofs: one ridge perch band per building (ridge along Y at the building centre).
  const byId = new Map(structural.map(box => [String(box.id ?? ''), box]));
  for (const [key, binding] of Object.entries(shape.authoring?.buildingRoofs ?? {})) {
    if (!binding?.inventoryId) continue;
    const roomIds = Array.isArray(binding.roomIds) ? binding.roomIds.map(String) : [];
    const members = roomIds.map(id => byId.get(id));
    if (!members.length || members.some(member => !member || member.creation?.roofRemoved === true)) continue;
    const kit = ROOF_RIDGE_KITS[String(binding.meshId ?? '').toUpperCase()];
    const [eave, cx, cy, depth] = [binding.z, binding.x, binding.y, binding.depthFt].map(Number) as [number, number, number, number];
    if (!kit || ![eave, cx, cy].every(Number.isFinite) || !(depth > 0)) continue;
    const level = eave + kit.standFt;
    const halfLength = kit.ridgeHalfFt === null ? depth / 2 : Math.max(.5, kit.ridgeHalfFt * depth / 30);
    decks.push({ id: `roof-ridge:${key}`, x: { min: cx - ROOF_RIDGE_HALF_WIDTH_FT, max: cx + ROOF_RIDGE_HALF_WIDTH_FT },
      y: { min: cy - halfLength, max: cy + halfLength }, floor: level, ceiling: level + 1000, roofOf: key, roofDeck: true,
      ridge: true, hipped: kit.ridgeHalfFt !== null, eaveFt: eave, roomIds, roofInventoryId: String(binding.inventoryId).toUpperCase(),
      roofFootprint: { minX: cx - (Number(binding.widthFt) || 0) / 2, maxX: cx + (Number(binding.widthFt) || 0) / 2, minY: cy - depth / 2, maxY: cy + depth / 2 } });
  }
  for (const box of structural) {
    const creation = box.creation ?? {};
    if (!String(creation.enclosure ?? '').includes('ceiling') || creation.roofRemoved === true || bound.has(String(box.id ?? ''))) continue;
    const level = Number(creation.physicalCeilingFt ?? box.ceiling);
    if (!Number.isFinite(level)) continue;
    const [x0, x1, y0, y1] = [box.x.min, box.x.max, box.y.min, box.y.max];
    const footprint = (creation.footprint ?? {}) as { cornerRadiusFt?: unknown; cornerRadiiFt?: Record<string, unknown> };
    const radius = Math.min(Math.max(0, Number(footprint.cornerRadiusFt ?? 0) || 0,
      ...Object.values(footprint.cornerRadiiFt ?? {}).map(value => Number(value) || 0)), (x1 - x0) / 2, (y1 - y0) / 2);
    const skip = structural.some(other => {
      if (other === box) return false;
      if (Math.min(x1, other.x.max) - Math.max(x0, other.x.min) <= .01 || Math.min(y1, other.y.max) - Math.max(y0, other.y.min) <= .01) return false;
      const covers = other.x.min <= x0 + .01 && other.x.max >= x1 - .01 && other.y.min <= y0 + .01 && other.y.max >= y1 - .01;
      return (Math.abs(other.floor - level) <= .01 && covers) || (level > other.floor + .01 && level < other.ceiling - .01);
    });
    if (skip) continue;
    const rects: Array<[number, number, number, number]> = radius > .01
      ? [[x0, x1, y0 + radius, y1 - radius], [x0 + radius, x1 - radius, y0, y1]] : [[x0, x1, y0, y1]];
    rects.forEach(([a, b, c, d], index) => {
      if (b - a <= .01 || d - c <= .01) return;
      decks.push({ id: `roof-deck:${String(box.id ?? '')}:${index}`, x: { min: a, max: b }, y: { min: c, max: d },
        floor: level, ceiling: level + 1000, roofOf: String(box.id ?? ''), roofDeck: true });
    });
  }
  return decks;
}

/** Is every point of this footprint (1 ft grid, edges included) on one of these deck rectangles? */
export function footprintOnDecks(decks: readonly FloorBandBox[], footprint: { minX: number; maxX: number; minY: number; maxY: number }): boolean {
  const axis = (min: number, max: number): number[] => {
    const count = Math.max(1, Math.ceil(max - min));
    return Array.from({ length: count + 1 }, (_, i) => min + (max - min) * i / count);
  };
  return axis(footprint.minX, footprint.maxX).every(x => axis(footprint.minY, footprint.maxY).every(y => decks.some(deck =>
    x >= deck.x.min - 1e-6 && x <= deck.x.max + 1e-6 && y >= deck.y.min - 1e-6 && y <= deck.y.max + 1e-6)));
}

/** The roof deck under feet standing ON a roof at (x, y, z), or null. */
export function roofDeckAt(shape: Parameters<typeof roofDeckBoxes>[0], x: number, y: number, z: number): RoofDeckBox | null {
  return roofDeckBoxes(shape, z).find(box => Math.abs(z - box.floor) <= .05
    && x >= box.x.min - 1e-6 && x <= box.x.max + 1e-6 && y >= box.y.min - 1e-6 && y <= box.y.max + 1e-6) ?? null;
}

/** The roof deck level under feet standing ON a roof at (x, y, z), or null. */
export function roofDeckLevelAt(shape: Parameters<typeof roofDeckBoxes>[0], x: number, y: number, z: number): number | null {
  return roofDeckAt(shape, x, y, z)?.floor ?? null;
}

/** No containing room means scene ground (0), never a nearest remote floor or a story label. */
export function floorBandForPosition(boxes: readonly FloorBandBox[], x: number, y: number, z: number): number {
  return floorBandRoomForPosition(boxes, x, y, z)?.floor ?? 0;
}
